import { BookingDraftParameters } from '@/scopes/checkout/domain/types'
import { getAcceptedParamsFromUrl } from '@/scopes/checkout/groups/domain/getAcceptedParamsFromUrl'
import { UpdateAvailableRoomParameters } from '@/scopes/checkout/groups/infrastructure/api/types/APIAvailableRooms'
import {
  groupsCheckoutPassThroughParams,
  GroupsCheckoutPassThroughParams,
} from '@/scopes/checkout/groups/infrastructure/api/types/groupsCheckoutPassThroughParams'
import {
  createBrowserQueryStringKeepingParams,
} from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import {
  createQueryStringSerializer,
  QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import defaultFetch from 'isomorphic-unfetch'
import {
  AvailableRooms,
  UpdateRoomSelectionPayload,
  UpdateRoomSelectionResponse,
} from '../../domain/types/availableRooms'
import { AvailableRoomsResponseMapper, createAvailableRoomsResponseMapper } from './mappers/availableRoomsResponseMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer
  availableRoomsResponseMapper: AvailableRoomsResponseMapper
}

export function createGroupsCheckoutAPIClient(dependencies: Partial<Dependencies> = {}) {
  const browserQuery = createBrowserQueryStringKeepingParams<GroupsCheckoutPassThroughParams>({
    passThroughParams: groupsCheckoutPassThroughParams,
  })
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    availableRoomsResponseMapper = createAvailableRoomsResponseMapper(),
  } = dependencies as Dependencies

  async function getAvailableRooms(masterBookingNumber: string): Promise<AvailableRooms> {
    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getAcceptedParamsFromUrl(queryParams, groupsCheckoutPassThroughParams)
    const apiResponse = await fetch(buildAvailableRoomsURL(masterBookingNumber, passThroughParams), { credentials: 'include', referrerPolicy: 'no-referrer-when-downgrade' })
    const response = await apiResponse.json()
    return availableRoomsResponseMapper.map(response)
  }

  async function setRoomSelection(
    apiContext: BookingDraftParameters,
    updatedRoom: UpdateRoomSelectionPayload,
  ): Promise<UpdateRoomSelectionResponse> {
    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getAcceptedParamsFromUrl(queryParams, groupsCheckoutPassThroughParams)
    const url = buildUpdateRoomSelectionURL(apiContext, passThroughParams)

    const apiResponse = await fetch(url, {
      method: 'PATCH',
      body: JSON.stringify({
        assignationMethod: updatedRoom.assignationMethod,
        roomName: updatedRoom.name,
        roomType: updatedRoom.type,
        pinCode: updatedRoom.pinCode,
        isNewRoom: updatedRoom.isNewRoom,
        roomId: updatedRoom.roomId,
      }),
    })
    return apiResponse.json()
  }

  function buildUpdateRoomSelectionURL({ contextItemId, sid }: UpdateAvailableRoomParameters, passThroughParams: GroupsCheckoutPassThroughParams) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      ...passThroughParams,
    })
    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/UpdateRoom?${queryString}`
  }

  function buildAvailableRoomsURL(masterBookingNumber: string, passThroughParams: GroupsCheckoutPassThroughParams) {
    const queryString = queryStringSerializer.serialize({
      masterBookingNumber,
      ...passThroughParams,
    })
    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/GetAvailableRoomsForGroups?${queryString}`
  }

  return {
    getAvailableRooms,
    setRoomSelection,
  }
}
